import { useTranslation } from 'react-i18next';
import { allowedCountries } from '@/data/allowedCountries';
import type { Country } from './types';

/** Countries promoted to the top of the list for German-speaking users. */
const DACH_ISO = new Set(['AT', 'DE', 'CH']);

/**
 * Static metadata derived once from `allowedCountries`. Display names are
 * resolved per-render via the `vatCountries` namespace so the list reacts to
 * language changes; the English `name` field is kept as a defensive fallback.
 */
const COUNTRY_META: ReadonlyArray<{
  iso: string;
  prefix: string;
  fallbackName: string;
}> = Object.entries(allowedCountries).map(([iso, data]) => ({
  iso,
  prefix: `+${data.dialCode}`,
  fallbackName: data.name,
}));

const isGermanLocale = (language: string | undefined): boolean =>
  language?.toUpperCase().startsWith('DE') ?? false;

/**
 * Returns the list of supported countries with translated display names and
 * locale-aware alphabetical ordering. DACH countries (AT, DE, CH) are pinned
 * to the top of the list for German-speaking users.
 *
 * React Compiler memoizes the returned array — no `useMemo` needed.
 */
const useLocalizedCountries = (): Country[] => {
  const { t, i18n } = useTranslation(['vatCountries']);
  // Tests sometimes mock `useTranslation` with only `{ t }`, leaving `i18n`
  // undefined. Treat that as "no preferred locale" rather than throwing.
  const language = i18n?.language;

  const preferDACH = isGermanLocale(language);
  const collator = new Intl.Collator(language || undefined, {
    sensitivity: 'base',
  });

  const localized: Country[] = COUNTRY_META.map(
    ({ iso, prefix, fallbackName }) => ({
      iso,
      prefix,
      name: t(`vatCountries:${iso}`, { defaultValue: fallbackName }),
      rank: preferDACH && DACH_ISO.has(iso) ? 1 : 0,
    })
  );

  const ranked = localized
    .filter((c) => c.rank > 0)
    .sort((a, b) => b.rank - a.rank);
  const rest = localized
    .filter((c) => c.rank === 0)
    .sort((a, b) => collator.compare(a.name, b.name));

  return [...ranked, ...rest];
};

export default useLocalizedCountries;
